import * as THREE from 'three'

/**
 * Prepares scene for screenshot: hides background/shadow man, shows ground shadow
 * @param scene The Three.js Scene instance
 * @returns Array of objects that were modified, to restore later
 */
function prepareSceneForScreenshot(
  scene: THREE.Scene
): Array<{ object: THREE.Object3D; originalVisible: boolean }> {
  const modifiedObjects: Array<{
    object: THREE.Object3D
    originalVisible: boolean
  }> = []

  // Traverse scene to find background, shadow man, and ground shadow objects
  scene.traverse((object) => {
    if (object instanceof THREE.Object3D) {
      // Check userData markers set by GLBModel and GroundShadow components
      const isBackground = object.userData.isBackground
      const isShadowMan = object.userData.isShadowMan
      const isGroundShadow = object.userData.isGroundShadow
      const isChair = object.userData.isChair
      const isLamp = object.userData.isLamp
      const isMonitor = object.userData.isMonitor
      const isRoom = object.userData.isRoom

      if (
        isBackground ||
        isShadowMan ||
        isChair ||
        isLamp ||
        isMonitor ||
        isRoom
      ) {
        if (object.visible) {
          modifiedObjects.push({ object, originalVisible: object.visible })
          object.visible = false
        }
      }

      // Show ground shadow (it's hidden by default)
      if (isGroundShadow) {
        modifiedObjects.push({ object, originalVisible: object.visible })
        object.visible = true
      }
    }
  })

  return modifiedObjects
}

/**
 * Restores visibility of previously modified objects
 * @param modifiedObjects Array of objects to restore
 */
export function restoreSceneVisibility(
  modifiedObjects: Array<{ object: THREE.Object3D; originalVisible: boolean }>
): void {
  modifiedObjects.forEach(({ object, originalVisible }) => {
    object.visible = originalVisible
  })
}

/**
 * Closes all doors in the scene by finding hinge groups and setting them to closed state
 * @param scene The Three.js Scene instance
 */
export function closeAllDoors(scene: THREE.Scene): void {
  const hingeGroups: THREE.Group[] = []

  // Traverse scene to find all hinge groups
  scene.traverse((object) => {
    if (object.userData.isHingeGroup && object instanceof THREE.Group) {
      hingeGroups.push(object)
    }
  })

  // Close all doors immediately
  hingeGroups.forEach((hingeGroup) => {
    // Set rotation to 0 (closed position)
    hingeGroup.rotation.y = 0

    // Find doorGroup child to calculate correct base z-position
    // From Door.tsx: doorGroup.position.z = -zPosition (relative to hingeGroup)
    // Where zPosition = doorDepth - panelThickness (from calculateDoorPositions)
    // So baseZPosition = -doorGroup.position.z
    const doorGroup = hingeGroup.children.find(
      (child) => child.userData.isDoorGroup
    )

    if (doorGroup) {
      // Calculate the base z-position from doorGroup's relative position
      // doorGroup.position.z is negative of the base z-position
      const baseZPosition = -doorGroup.position.z

      // Set to closed position (remove any offset that might have been added by animation)
      hingeGroup.position.z = baseZPosition
    }

    // Update matrix to apply changes immediately
    hingeGroup.updateMatrixWorld(true)
  })
}

/**
 * Closes all drawers in the scene by resetting their z-position to the base value
 * @param scene The Three.js Scene instance
 */
export function closeAllDrawers(scene: THREE.Scene): void {
  scene.traverse((object) => {
    if (object.userData.isDrawerGroup && object instanceof THREE.Group) {
      const baseZ = object.userData.baseZ ?? 0
      object.position.z = baseZ
      object.updateMatrixWorld(true)
    }
  })
}

/**
 * Captures a screenshot from a Three.js WebGLRenderer
 * @param renderer The Three.js WebGLRenderer instance
 * @param scene The Three.js Scene instance
 * @param camera The Three.js Camera instance
 * @param format The image format ('image/png' or 'image/jpeg')
 * @param quality Quality for JPEG (0-1), ignored for PNG
 * @returns Base64 encoded image data URL
 */
export function captureScreenshot(
  renderer: THREE.WebGLRenderer,
  scene: THREE.Scene,
  camera: THREE.Camera,
  format: 'image/png' | 'image/jpeg' = 'image/png',
  quality: number = 0.92
): string {
  closeAllDoors(scene)
  closeAllDrawers(scene)

  // Prepare scene for screenshot: hide background/shadow man, show ground shadow
  const modifiedObjects = prepareSceneForScreenshot(scene)

  // Ensure the scene is rendered before capturing
  renderer.render(scene, camera)

  // Capture the canvas as data URL
  const dataURL = renderer.domElement.toDataURL(format, quality)

  // Restore scene visibility after capture
  restoreSceneVisibility(modifiedObjects)

  return dataURL
}

